import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { DropdownOption } from "../components/ui-components/dropdown/dropdown.model";

@Injectable({
  providedIn: "root",
})
export class StateDropdownService {
  private _http = inject(HttpClient);

  private readonly fallbackStateLabelsCz: Record<string, string> = {
    CZE: "Česko",
    SVK: "Slovensko",
    UKR: "Ukrajina",
  };

  private readonly fallbackStateLabelsEn: Record<string, string> = {
    CZE: "Czechia",
    SVK: "Slovakia",
    UKR: "Ukraine",
  };

  private stateOptionsCache: DropdownOption[] | null = null;

  async getStateOptions(currentValue?: string): Promise<DropdownOption[]> {
    const baseOptions = await this.loadStateOptions();
    return this.ensureCountryInOptions(baseOptions, currentValue);
  }

  private async loadStateOptions(): Promise<DropdownOption[]> {
    if (this.stateOptionsCache) {
      return this.stateOptionsCache;
    }

    const priority = ["CZE", "SVK", "UKR"];

    try {
      const stateData = await firstValueFrom(
        this._http.get<
          string[] | {
            codes: string[];
            labelsCz?: Record<string, string>;
            labelsEn?: Record<string, string>;
          }
        >("/assets/data/iso3-states.json")
      );

      const isArrayData = Array.isArray(stateData);
      const codes = isArrayData ? stateData : stateData?.codes || [];
      const labelsCz = isArrayData
        ? this.fallbackStateLabelsCz
        : {
            ...this.fallbackStateLabelsCz,
            ...(stateData?.labelsCz || {}),
          };
      const labelsEn = isArrayData
        ? this.fallbackStateLabelsEn
        : {
            ...this.fallbackStateLabelsEn,
            ...(stateData?.labelsEn || {}),
          };

      const uniqueCodes = Array.from(
        new Set(
          (codes || [])
            .map((code) => (code || "").toUpperCase().trim())
            .filter(Boolean)
        )
      );

      uniqueCodes.sort((a, b) => {
        const aPriority = priority.indexOf(a);
        const bPriority = priority.indexOf(b);

        if (aPriority !== -1 && bPriority !== -1) {
          return aPriority - bPriority;
        }
        if (aPriority !== -1) {
          return -1;
        }
        if (bPriority !== -1) {
          return 1;
        }

        return a.localeCompare(b);
      });

      this.stateOptionsCache = uniqueCodes.map((code) => ({
        value: code,
        label: `${code} - ${this.getStateDisplayName(code, labelsCz, labelsEn)}`,
      }));
    } catch (error) {
      this.stateOptionsCache = priority.map((code) => ({
        value: code,
        label: `${code} - ${this.getStateDisplayName(
          code,
          this.fallbackStateLabelsCz,
          this.fallbackStateLabelsEn
        )}`,
      }));
    }

    return this.stateOptionsCache;
  }

  private ensureCountryInOptions(
    options: DropdownOption[],
    value?: string
  ): DropdownOption[] {
    const currentState = (value || "").toUpperCase().trim();
    if (!currentState) {
      return options;
    }

    if (options.some((option) => option.value === currentState)) {
      return options;
    }

    const fallbackOption = {
      value: currentState,
      label: `${currentState} - ${this.getStateDisplayName(
        currentState,
        this.fallbackStateLabelsCz,
        this.fallbackStateLabelsEn
      )}`,
    };

    return [fallbackOption, ...options];
  }

  private getStateDisplayName(
    code: string,
    labelsCz: Record<string, string>,
    labelsEn: Record<string, string>
  ): string {
    return (
      labelsCz[code] ||
      labelsEn[code] ||
      this.getIntlEnglishStateName(code) ||
      code
    );
  }

  private getIntlEnglishStateName(code: string): string | null {
    try {
      const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
      const name = displayNames.of(code);
      if (!name || name === code) {
        return null;
      }
      return name;
    } catch {
      return null;
    }
  }

  clearCache(): void {
    this.stateOptionsCache = null;
  }
}
